import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CatalogItem } from "@apex/shared";
import { cn, formatPrice } from "@apex/shared";
import { useWidget } from "../state";
import { Button } from "./ui/Button";

export interface PersistentSummaryPanelProps {
  className?: string;
}

interface CategoryGroup {
  category: string;
  items: CatalogItem[];
  total: number;
}

/**
 * Persistent summary panel rendered alongside the Configure (Step 2) and
 * Summary (Step 3) views (#540). Replaces the legacy `SummaryPanel` +
 * Step-3 SummaryView duality with a single component used at all
 * viewports.
 *
 * Responsive treatment:
 *  - lg+ (≥1024px): sticky right column (~320px)
 *  - md → lg: sticky right column (~280px)
 *  - <md: sticky-bottom collapsed bar; tap chevron expands to a full-height
 *    drawer with backdrop, focus trap, Esc-to-close.
 *
 * Pricing-aware (#533): when `state.rootHasPricing === false`, totals are
 * suppressed and an item count line is shown instead.
 */
export function PersistentSummaryPanel({ className }: PersistentSummaryPanelProps) {
  const {
    state,
    totalPrice,
    selectedItemList,
    viewSummary,
    submitQuote,
    scrollToItem,
    toggleItem,
  } = useWidget();
  const { selectedRootItem, categories, view, rootHasPricing } = state;

  const [mobileExpanded, setMobileExpanded] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Esc closes the mobile drawer
  useEffect(() => {
    if (!mobileExpanded) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setMobileExpanded(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [mobileExpanded]);

  // Focus trap: focus the dialog on open
  useEffect(() => {
    if (mobileExpanded && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [mobileExpanded]);

  // Group selected items by category for display
  const groupedItems = useMemo((): CategoryGroup[] => {
    if (selectedItemList.length === 0) return [];

    const categoryMap = new Map<string, CatalogItem[]>();

    const sortedCategories = [...categories].sort(
      (a, b) => a.displayOrder - b.displayOrder
    );
    const categoryNames = new Map(
      sortedCategories.map((c) => [String(c.id), c.name])
    );

    for (const item of selectedItemList) {
      const catId =
        item.categoryId != null ? String(item.categoryId) : "__other__";
      const catName = categoryNames.get(catId) || item.categoryName || "Other";

      if (!categoryMap.has(catName)) {
        categoryMap.set(catName, []);
      }
      categoryMap.get(catName)!.push(item);
    }

    return Array.from(categoryMap.entries()).map(([category, items]) => ({
      category,
      items,
      total: items.reduce((sum, a) => sum + (a.priceNet ?? 0), 0),
    }));
  }, [selectedItemList, categories]);

  if (!selectedRootItem) {
    return null;
  }

  const isStep3 = view === "summary";
  const itemCount = selectedItemList.length;
  // Anchor the summary currency on the root item — it always has a
  // currency stamped from the catalog, while `selectedItemList` only
  // holds accessories the user picked (and might be empty in step 2
  // before any selection). Without this anchor, `formatPrice` falls
  // back to EUR and the summary drifts away from the model's GBP.
  const currency =
    selectedRootItem.currency ??
    selectedItemList.find((a) => a.currency)?.currency;

  // VAT@20% — illustrative; true VAT lives in the quote on the server.
  const subtotal = totalPrice;
  const vat = rootHasPricing ? subtotal * 0.2 : 0;
  const total = subtotal + vat;

  const ctaLabel = isStep3
    ? "Submit quote request"
    : "Continue to your details";

  const handleCta = () => {
    if (isStep3) {
      void submitQuote();
    } else {
      viewSummary();
    }
  };

  const handleRowClick = useCallback(
    (id: string) => {
      scrollToItem(id);
      setMobileExpanded(false);
    },
    [scrollToItem]
  );

  const handleRemove = useCallback(
    (id: string) => {
      toggleItem(id);
    },
    [toggleItem]
  );

  const renderBody = (compact: boolean) => (
    <div className="apex-flex apex-flex-col apex-h-full">
      <div
        className={cn(
          "apex-shrink-0 apex-px-4 apex-pt-3 apex-pb-2 apex-border-b apex-border-border"
        )}
      >
        <h3
          id="apex-summary-panel-title"
          className="apex-font-semibold apex-text-foreground apex-font-display"
        >
          Summary
        </h3>
        <p className="apex-text-xs apex-text-muted-foreground apex-mt-0.5 apex-truncate">
          {selectedRootItem.name || selectedRootItem.code}
        </p>
      </div>

      <div className="apex-flex-1 apex-min-h-0 apex-overflow-y-auto apex-px-4 apex-py-3">
        {itemCount === 0 ? (
          <div
            className="apex-text-center apex-py-6"
            data-testid="persistent-panel-empty"
          >
            <p className="apex-text-sm apex-text-muted-foreground">
              Add accessories to your model
            </p>
          </div>
        ) : (
          <>
            {!rootHasPricing && (
              <p className="apex-text-xs apex-text-muted-foreground apex-mb-3">
                {itemCount} {itemCount === 1 ? "item" : "items"} selected
              </p>
            )}
            <ul className="apex-space-y-3">
              {groupedItems.map((group) => (
                <li key={group.category}>
                  <p className="apex-text-[11px] apex-uppercase apex-tracking-wider apex-text-muted-foreground apex-font-medium apex-mb-1.5">
                    {group.category}
                  </p>
                  <ul className="apex-space-y-1">
                    {group.items.map((item) => (
                      <li
                        key={item.id}
                        className="apex-flex apex-items-center apex-justify-between apex-gap-2 apex-text-sm"
                      >
                        <button
                          type="button"
                          className="apex-flex-1 apex-min-w-0 apex-min-h-6 apex-py-0.5 apex-truncate apex-text-left apex-text-foreground hover:apex-text-primary apex-transition-colors apex-rounded focus-visible:apex-outline-none focus-visible:apex-ring-2 focus-visible:apex-ring-ring"
                          aria-label={`Scroll to ${item.name || item.code}`}
                          onClick={() => handleRowClick(item.id)}
                        >
                          {item.name || item.code}
                        </button>
                        <div className="apex-flex apex-items-center apex-gap-1.5 apex-shrink-0">
                          {rootHasPricing &&
                            item.priceNet != null &&
                            item.priceNet > 0 && (
                              <span className="apex-text-xs apex-text-muted-foreground apex-tabular-nums">
                                {formatPrice(item.priceNet, item.currency)}
                              </span>
                            )}
                          <button
                            type="button"
                            aria-label={`Remove ${item.name || item.code}`}
                            onClick={() => handleRemove(item.id)}
                            // 28px square: WCAG 2.5.8 needs a 24px minimum target.
                            className="apex-shrink-0 apex-w-7 apex-h-7 apex-rounded apex-flex apex-items-center apex-justify-center apex-text-muted-foreground hover:apex-text-destructive hover:apex-bg-destructive/10 apex-transition-colors focus-visible:apex-outline-none focus-visible:apex-ring-2 focus-visible:apex-ring-ring"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <path d="M18 6 6 18" />
                              <path d="m6 6 12 12" />
                            </svg>
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <div className="apex-shrink-0 apex-px-4 apex-py-3 apex-border-t apex-border-border apex-bg-muted/20">
        {rootHasPricing ? (
          <div className="apex-space-y-1 apex-mb-3">
            <Row label="Subtotal" value={formatPrice(subtotal, currency)} />
            <Row label="VAT (20%)" value={formatPrice(vat, currency)} />
            <Row
              label="Total"
              value={formatPrice(total, currency)}
              emphasis
            />
          </div>
        ) : (
          <p className="apex-text-xs apex-text-muted-foreground apex-mb-3 apex-text-center">
            Final pricing provided in your quote
          </p>
        )}
        <Button
          onClick={handleCta}
          variant="cta"
          size={compact ? "sm" : "lg"}
          className="apex-w-full"
        >
          {ctaLabel}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop / tablet sidebar — md+ */}
      <aside
        aria-labelledby="apex-summary-panel-title"
        className={cn(
          "apex-hidden md:apex-flex apex-flex-col apex-shrink-0",
          "apex-border-l apex-border-border apex-bg-background",
          // `self-start` so the aside keeps its own height inside the flex
          // row (a stretched item has nothing to stick within). `h-full`
          // still fills a fixed-height embed, where the row is bounded.
          "apex-sticky apex-self-start apex-h-full",
          "md:apex-w-[280px] lg:apex-w-[320px]",
          className
        )}
        // Pin below the host page's fixed header (sticky-top attribute), and
        // never taller than the viewport beneath it so the item list scrolls
        // internally and the CTA stays on screen (auto embeds).
        style={{
          top: "var(--apex-sticky-offset, 0px)",
          maxHeight: "calc(100vh - var(--apex-sticky-offset, 0px))",
        }}
      >
        {renderBody(false)}
      </aside>

      {/* Mobile sticky-bottom bar (collapsed) */}
      <div className="md:apex-hidden">
        <div
          className="apex-fixed apex-inset-x-0 apex-bottom-0 apex-z-30 apex-border-t apex-border-border apex-bg-background"
          style={{ boxShadow: "0 -4px 12px rgba(0,0,0,0.06)" }}
        >
          <button
            type="button"
            aria-label="See summary"
            aria-expanded={mobileExpanded}
            onClick={() => setMobileExpanded(true)}
            className="apex-w-full apex-flex apex-items-center apex-justify-between apex-px-4 apex-py-3 apex-text-sm"
          >
            <span className="apex-font-medium apex-text-foreground">
              {itemCount === 0
                ? "Summary"
                : `${itemCount} ${itemCount === 1 ? "item" : "items"} selected · See summary`}
            </span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="m18 15-6-6-6 6" />
            </svg>
          </button>
        </div>

        {/* Expanded drawer */}
        {mobileExpanded && (
          <>
            <div
              aria-hidden="true"
              onClick={() => setMobileExpanded(false)}
              className="apex-fixed apex-inset-0 apex-z-40 apex-bg-foreground/40"
            />
            <div
              ref={dialogRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="apex-summary-panel-title"
              tabIndex={-1}
              className="apex-fixed apex-inset-x-0 apex-bottom-0 apex-z-50 apex-bg-background apex-rounded-t-xl apex-border-t apex-border-border"
              style={{ height: "85vh" }}
            >
              <button
                type="button"
                aria-label="See summary"
                aria-expanded={mobileExpanded}
                onClick={() => setMobileExpanded(false)}
                className="apex-w-full apex-flex apex-items-center apex-justify-center apex-py-2 apex-text-muted-foreground"
              >
                <span className="apex-w-10 apex-h-1 apex-rounded-full apex-bg-border" />
                <span className="apex-sr-only">See summary</span>
              </button>
              {renderBody(true)}
            </div>
          </>
        )}
      </div>
    </>
  );
}

function Row({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div
      className={cn(
        "apex-flex apex-items-baseline apex-justify-between apex-text-sm",
        emphasis && "apex-pt-1 apex-border-t apex-border-border apex-mt-1"
      )}
    >
      <span
        className={cn(
          "apex-text-muted-foreground",
          emphasis && "apex-font-semibold apex-text-foreground"
        )}
      >
        {label}
      </span>
      <span
        className={cn(
          "apex-tabular-nums",
          emphasis
            ? "apex-text-base apex-font-bold apex-text-primary"
            : "apex-text-foreground"
        )}
      >
        {value}
      </span>
    </div>
  );
}
